<template>
  <div v-if="renderable">
    <div
      v-if="registrationNotYetOpen"
      data-test="eventRegisterOpeningInfo"
      class="text-center register-opening-info"
    >
      <div>
        <span :style="buttonTextStyle">{{
          $t("common:button.register", "Register")
        }}</span>
      </div>
      <small style="font-weight: normal">
        {{ fromText }}
        {{ $dayjs.date(event.publicRegistrationOpensFrom) }}
      </small>
    </div>
    <v-btn
      v-else
      ref="registerBtn"
      color="accent"
      v-bind="{ ...$attrs, to, href, target }"
      data-test="eventRegisterBtn"
      class="text-center register-btn white--text"
    >
      <div class="d-flex" style="flex-direction: column">
        <div :class="{ 'mb-1': !event.registrationEndDatesPerCompetition }">
          <span :style="buttonTextStyle">{{ buttonText }}</span>
          <v-icon v-if="href" color="white" x-small class="ml-2"
            >$iconExternalLink</v-icon
          >
        </div>
        <small
          v-if="!event.registrationEndDatesPerCompetition"
          style="font-weight: normal"
        >
          {{
            $t(
              "common:register.until.label",
              "Until:",
              "Register butten label Until:"
            )
          }}
          {{ $dayjs.date(event.registrationEndDate) }}
        </small>
      </div>
    </v-btn>
  </div>
</template>
<script>
import { inject, defineComponent } from "vue";
import gql from "fraql";
import { useEventUrlHelpers } from "@/components/event/mixins/eventUrlMixin";
import { getFederationConfiguration } from "@composables/masterdata/config";
import { truncate } from "lodash-es";

export default defineComponent({
  name: "AppShowRegistrationBtn",
  inject: {
    isNoncompetition: {
      default: false
    },
    registrationBtnForceClick: {
      default: false
    }
  },
  props: {
    event: {
      type: Object,
      required: true
    }
  },
  setup(props) {
    const embeddedMode = inject("embeddedMode", false);
    const { externalRegistrationUrl } = useEventUrlHelpers(props.event);
    return {
      embeddedMode,
      ...getFederationConfiguration(),

      externalRegistrationUrl
    };
  },
  computed: {
    fromText() {
      return truncate(this.$t("common:register.from.label", "From:"), {
        length: 12
      });
    },
    registrationNotYetOpen() {
      return (
        this.event.status == "registration_open" &&
        this.event.publicRegistrationOpensFrom &&
        this.$dayjs(this.event.publicRegistrationOpensFrom).utc() >
          this.$dayjs().utc()
      );
    },
    buttonText() {
      return this.$t("common:button.register", "Register");
    },
    buttonTextStyle() {
      return this.buttonText.length >= 18 ? "font-size: 14px;" : undefined;
    },
    renderable() {
      if (
        this.masterConfiguration.feiPublicRegistrationDisabled &&
        this.event.usesMultiRegistration
      ) {
        return this.$allowedMutation(
          this.event.allowedMutations,
          "multiRegistrationCreateOrUpdate"
        );
      }

      return this.event.status == "registration_open";
    },
    pageName() {
      if (this.isNoncompetition) {
        return "NoncompetitionEventRegistrationPage";
      }

      if (this.event.usesMultiRegistration) {
        return "EventMultiRegistrationPage";
      }

      return "EventRegistrationPage";
    },
    to() {
      if (this.event.externalRegistrationUrl) return undefined;
      return {
        name: this.pageName,
        params: {
          eventId: this.event.id,
          returnRoute: {
            name: this.$route.name,
            routeScrollState: {
              routeName: this.$route.name,
              identifier: this.event.id
            }
          }
        }
      };
    },
    target() {
      if (this.embeddedMode) return "_blank";
      if (!this.event.externalRegistrationUrl) return undefined;
      return "_blank";
    },
    href() {
      if (!this.event.externalRegistrationUrl) return undefined;
      return this.externalRegistrationUrl;
    }
  },
  mounted() {
    if (this.registrationBtnForceClick && this.$refs.registerBtn) {
      // In EventCard and calendar, whole card/row is clickable
      // Meaning clicking on this component's link gets screwed over by closest <a> link element
      // stopPropagation stops that being triggered, allowing to open in new tab
      const btn = this.$refs.registerBtn.$el;
      btn.addEventListener("click", evt => {
        evt.stopPropagation();
      });
    }
  },
  fragments: {
    event: gql`
      fragment _ on Event {
        id
        noncompetition
        usesMultiRegistration
        externalRegistrationUrl
        registrationEndDate
        registrationEndDatesPerCompetition
        publicRegistrationOpensFrom
      }
    `,
    // This fragment to be only used on single event query, not in events query
    eventWithMutations: gql`
      fragment _ on Event {
        id
        noncompetition
        usesMultiRegistration
        externalRegistrationUrl
        registrationEndDate
        registrationEndDatesPerCompetition
        allowedMutations
        publicRegistrationOpensFrom
      }
    `
  }
});
</script>
<style scoped>
.register-opening-info {
  width: 161px;
  height: 50px;

  /* vuetify code */
  min-width: 64px;
  padding: 0 16px;

  background-color: var(--v-primary-light-base);
  box-shadow:
    0px 3px 1px -2px rgba(0, 0, 0, 0.2),
    0px 2px 2px 0px rgba(0, 0, 0, 0.14),
    0px 1px 5px 0px rgba(0, 0, 0, 0.12);
}
</style>
